import { FabricImage } from "fabric";

/**
 * Loads a remote image for Fabric without tainting the canvas (so toDataURL / save works).
 * Uses CORS first; if that fails, fetches as blob and loads from a blob URL (same-origin for the bitmap).
 */
export async function loadFabricImageForCanvas(url) {
  if (!url || typeof url !== "string") {
    throw new Error("Invalid image URL");
  }
  try {
    return await FabricImage.fromURL(url, { crossOrigin: "anonymous" });
  } catch {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) {
      throw new Error(`Could not load image (${res.status})`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
      return await FabricImage.fromURL(blobUrl, {});
    } finally {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
    }
  }
}

/** Ensures serialized Fabric JSON loads images with crossOrigin so export is not tainted. */
export function ensureFabricJsonImagesCrossOrigin(root) {
  if (!root || typeof root !== "object") return;
  const walk = (o) => {
    if (!o || typeof o !== "object") return;
    const t = (o.type || "").toLowerCase();
    if (t === "image" || t === "fabricimage" || t === "invitationfabricvideo") {
      o.crossOrigin = "anonymous";
    }
    if (Array.isArray(o.objects)) {
      o.objects.forEach(walk);
    }
  };
  if (Array.isArray(root.objects)) {
    root.objects.forEach(walk);
  }
  walk(root.backgroundImage);
  walk(root.overlayImage);
}

function isTextType(obj) {
  if (!obj) return false;
  const type = (obj.type || "").toLowerCase();
  return (
    type === "textbox" ||
    type === "itext" ||
    type === "i-text" ||
    type === "text"
  );
}

/**
 * Resolve the Fabric object to use for typography tools.
 * Handles ActiveSelection and normal Textbox / IText / Text.
 */
export function getInspectableTextObject(activeObject) {
  if (!activeObject) return null;
  if (isTextType(activeObject)) return activeObject;

  const type = (activeObject.type || "").toLowerCase();
  if (type === "activeselection") {
    const objects =
      typeof activeObject.getObjects === "function"
        ? activeObject.getObjects()
        : activeObject._objects || [];
    const first = objects.find((o) => isTextType(o));
    return first || null;
  }

  return null;
}

export function isTextLikeObject(obj) {
  return getInspectableTextObject(obj) != null;
}
